import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Copy,
  Eye,
  ArrowUpFromLine,
  History,
  Trash2,
  Loader2,
  Sparkles,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A/B / Draft controls for a site page. Clones the current live page into a
// `<slug>__draft` document that admins can edit safely, and provides Promote /
// Revert flows once the draft is ready to ship.
//
// Draft URL: /admin/site-pages/<slug>__draft (open in a new tab)
// Preview URL: /preview/<slug> (renders the draft in the public shell)
export default function SitePageDraftPanel({ slug }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);

  const isDraftSlug = slug.endsWith("__draft") || slug.endsWith("__backup");
  const baseSlug = isDraftSlug ? slug.replace(/__draft$|__backup$/, "") : slug;

  const load = async () => {
    try {
      const { data } = await axios.get(
        `${API}/admin/site-pages/${baseSlug}/draft-status`
      );
      setStatus(data);
    } catch {
      // Silent — the panel simply won't render draft actions.
    }
  };

  useEffect(() => {
    load();
  }, [baseSlug]);

  const call = async (verb, path, successMsg) => {
    setBusy(true);
    try {
      await axios[verb](`${API}/admin/site-pages/${baseSlug}/${path}`);
      toast.success(successMsg);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Request failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <section
      className="bg-white border border-slate-200 rounded-xl p-5"
      data-testid="draft-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4 text-amber-600" />
          </div>
          <div className="text-left min-w-0">
            <h2 className="text-sm font-bold text-slate-900">Draft &amp; Preview</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {isDraftSlug
                ? "You are editing a draft — changes won't affect the live page until you Promote."
                : status.has_draft
                ? `Draft exists (updated ${new Date(status.draft_updated_at).toLocaleString()}).`
                : "Clone the current live page to a draft, edit safely, then promote."}
            </p>
          </div>
        </div>
        <span className="shrink-0 text-slate-400">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
      </button>

      {open && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {!isDraftSlug && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() =>
                  call(
                    "post",
                    "clone-draft",
                    status.has_draft
                      ? "Draft refreshed from live page."
                      : "Draft created."
                  )
                }
                data-testid="draft-clone-btn"
                className="h-8 text-xs"
              >
                {busy ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <Copy className="h-3.5 w-3.5 mr-1" />
                )}
                {status.has_draft ? "Refresh draft from live" : "Clone to draft"}
              </Button>
              {status.has_draft && (
                <>
                  <Link
                    to={`/admin/site-pages/${baseSlug}__draft`}
                    data-testid="draft-edit-link"
                    className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:text-sky-700 transition h-8"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Edit draft
                  </Link>
                  <a
                    href={`/preview/${baseSlug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="draft-preview-link"
                    className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:text-sky-700 transition h-8"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Preview draft
                  </a>
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      if (!window.confirm("Promote the draft into the live page? A backup of the current live page will be saved.")) return;
                      call("post", "promote-draft", "Draft promoted to live. Backup saved.");
                    }}
                    data-testid="draft-promote-btn"
                    className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <ArrowUpFromLine className="h-3.5 w-3.5 mr-1" />
                    Promote draft to live
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      if (!window.confirm("Delete this draft? Cannot be undone.")) return;
                      call("delete", "draft", "Draft discarded.");
                    }}
                    data-testid="draft-discard-btn"
                    className="h-8 text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Discard draft
                  </Button>
                </>
              )}
              {status.has_backup && (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      if (!window.confirm("Revert the live page to the last backup? This will overwrite the current live page.")) return;
                      call("post", "revert", "Live page reverted to the last backup.");
                    }}
                    data-testid="draft-revert-btn"
                    className="h-8 text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
                  >
                    <History className="h-3.5 w-3.5 mr-1" />
                    Revert to backup
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      if (!window.confirm("Delete the last backup? Revert will no longer be available.")) return;
                      call("delete", "backup", "Backup deleted.");
                    }}
                    data-testid="draft-delete-backup-btn"
                    className="h-8 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Delete backup
                  </Button>
                </>
              )}
            </>
          )}
          {isDraftSlug && (
            <Link
              to={`/admin/site-pages/${baseSlug}`}
              data-testid="draft-return-live-link"
              className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:text-sky-700 transition h-8"
            >
              ← Back to live editor
            </Link>
          )}
        </div>
      )}
    </section>
  );
}
